import { inject } from '@aesop-fables/containr';
import { ApiServices } from '../ApiServices';
import type { AxiosInstance, AxiosResponse, AxiosRequestConfig } from 'axios';
import {
  GroupTransactionsData,
  OrgTransactionsData,
  RevShareEntry,
  RevShareResult,
  TeamTransactionsData,
} from '../../models/RevShareData';

export class RevShareApi {
  private readonly csvConfig: AxiosRequestConfig = {
    headers: {
      Accept: 'text/csv',
    },
    responseType: 'arraybuffer',
  };

  private readonly pdfConfig: AxiosRequestConfig = {
    headers: {
      Accept: 'application/pdf',
    },
    responseType: 'arraybuffer',
  };

  constructor(@inject(ApiServices.Axios) private axios: AxiosInstance) {}

  getTeamRevShare(
    startDate: string,
    endDate: string,
  ): Promise<AxiosResponse<TeamTransactionsData>> {
    return this.axios.get('/revenue-share/by-team', { params: { startDate, endDate } });
  }

  getOrgRevShare(startDate: string, endDate: string): Promise<AxiosResponse<OrgTransactionsData>> {
    return this.axios.get('/revenue-share/by-org', { params: { startDate, endDate } });
  }

  getGroupRevShare(
    startDate: string,
    endDate: string,
  ): Promise<AxiosResponse<GroupTransactionsData>> {
    return this.axios.get('/revenue-share/by-group', { params: { startDate, endDate } });
  }

  getAllRevShareEntries(): Promise<AxiosResponse<RevShareEntry[]>> {
    return this.axios.get('/revenue-share/all');
  }

  getTeamRevShareResultByPricebook(pricebookId: number): Promise<AxiosResponse<RevShareResult>> {
    return this.axios.get('/revenue-share/by-team/by-pricebookId/rev-share-result', {
      params: { pricebookId },
    });
  }

  getOrgRevShareResultByPricebook(pricebookId: number): Promise<AxiosResponse<RevShareResult>> {
    return this.axios.get('/revenue-share/by-org/by-pricebookId/rev-share-result', {
      params: { pricebookId },
    });
  }

  private generateCsvFile(
    endpoint: string,
    startDate: string,
    endDate: string,
  ): Promise<AxiosResponse> {
    return this.axios.post(
      `${endpoint}?startDate=${startDate}&endDate=${endDate}`,
      {},
      this.csvConfig,
    );
  }

  private generatePdfFile(
    endpoint: string,
    startDate: string,
    endDate: string,
  ): Promise<AxiosResponse> {
    const params = new URLSearchParams({ startDate, endDate });
    return this.axios.post(`${endpoint}?${params.toString()}`, {}, this.pdfConfig);
  }

  generateTeamRevShareCsv(startDate: string, endDate: string): Promise<AxiosResponse> {
    return this.generateCsvFile('/revenue-share/by-team/rev-shares/file', startDate, endDate);
  }

  generateTeamChargesCsv(startDate: string, endDate: string): Promise<AxiosResponse> {
    return this.generateCsvFile('/revenue-share/by-team/charges/file', startDate, endDate);
  }

  generateOrgRevShareCsv(startDate: string, endDate: string): Promise<AxiosResponse> {
    return this.generateCsvFile('/revenue-share/by-org/rev-shares/file', startDate, endDate);
  }

  generateOrgChargesCsv(startDate: string, endDate: string): Promise<AxiosResponse> {
    return this.generateCsvFile('/revenue-share/by-org/charges/file', startDate, endDate);
  }

  generateGroupRevShareCsv(startDate: string, endDate: string): Promise<AxiosResponse> {
    return this.generateCsvFile('/revenue-share/by-group/rev-shares/file', startDate, endDate);
  }

  generateGroupChargesCsv(startDate: string, endDate: string): Promise<AxiosResponse> {
    return this.generateCsvFile('/revenue-share/by-group/charges/file', startDate, endDate);
  }

  generateTeamRevSharePdf(startDate: string, endDate: string): Promise<AxiosResponse> {
    return this.generatePdfFile('/revenue-share/by-team/pdf', startDate, endDate);
  }

  generateOrgRevSharePdf(startDate: string, endDate: string): Promise<AxiosResponse> {
    return this.generatePdfFile('/revenue-share/by-org/pdf', startDate, endDate);
  }

  generateGroupRevSharePdf(startDate: string, endDate: string): Promise<AxiosResponse> {
    return this.generatePdfFile('/revenue-share/by-group/pdf', startDate, endDate);
  }

  addRevenueShare(data: AddRevSharePayload): Promise<AxiosResponse<void>> {
    return this.axios.post('/revenue-share/add', data);
  }

  addDisabledRevShare(
    pricebookId: number,
    effectiveTimestamp?: string,
    expireTimestamp?: string,
  ): Promise<AxiosResponse<void>> {
    return this.axios.post('/revenue-share/add/disabled-rev-share', null, {
      params: { pricebookId, effectiveTimestamp, expireTimestamp },
    });
  }

  deleteRevenueShare(id: number): Promise<AxiosResponse<void>> {
    return this.axios.delete(`/revenue-share/${id}`);
  }

  deleteAllByPricebook(pricebookId: number): Promise<AxiosResponse<void>> {
    return this.axios.delete('/revenue-share/all/by-pricebook', { params: { pricebookId } });
  }

  patchRevenueShare(id: number, data: PatchRevSharePayload): Promise<AxiosResponse<void>> {
    return this.axios.patch(`/revenue-share/${id}`, data);
  }

  overrideRevenueShare(pricebookId: number): Promise<AxiosResponse<RevShareEntry[]>> {
    return this.axios.post('/revenue-share/override', null, { params: { pricebookId } });
  }
}

export interface AddRevSharePayload {
  pricebookId: number;
  baseAmount: number;
  revSharePct: number;
  fixedAmount: number;
  revShareDorp: string;
  tier: string;
  effectiveTimestamp?: string;
  expireTimestamp?: string;
}

export type PatchRevSharePayload = Omit<AddRevSharePayload, 'tier'> & {
  clearExpireTimestamp?: boolean;
};
